import { parseISO } from "date-fns";
import voca from "voca";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { type DateFormatter, formatDuration } from "@/lib/format";
import type { ActivityItem } from "@/types/messages";
import { UserAvatar } from "../UserAvatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

function getAttributeName(activityItem: ActivityItem) {
    switch (activityItem.update!.attributeId) {
        case "title":
            return "Title";
        case "dueDate":
            return "Due date";
        case "status":
            return "Status";
        case "priority":
            return "Priority";
        case "assignee":
            return "Assignee";
        case "lists":
            return "Lists";
        case "createdAt":
            return "Created at";
        default:
            return activityItem.update!.attributeName;
    }
}

function getActivityItemValue(activityItem: ActivityItem, formatter: DateFormatter): string {
    if (activityItem.update!.attributeType === "timer") {
        if (!activityItem.update!.attributeValue) return "";
        return formatDuration(Number(activityItem.update!.attributeValue));
    }

    switch (activityItem.update!.attributeId) {
        case "dueDate":
            if (!activityItem.update!.attributeValue) return "No due date";
            return formatter.formatDate(parseISO(activityItem.update!.attributeValue));
        case "priority":
            if (!activityItem.update!.attributeValue) return "None";
            return voca.titleCase(activityItem.update!.attributeValue);
        default:
            if (!activityItem.update!.attributeValue) return "";
            return activityItem.update!.attributeValue;
    }
}

export default function TaskUpdate({ activityItem }: { activityItem: ActivityItem }) {
    const isCreation = activityItem.update!.attributeId === "created";
    const attributeName = getAttributeName(activityItem);
    const formatter = useDateFormatter();
    return (
        <Tooltip>
            <TooltipTrigger
                delay={1000}
                render={
                    <div className="text-xs text-zinc-400 mb-1 flex items-center gap-1 w-full text-center justify-center">
                        <UserAvatar user={activityItem.user} size="xs" />
                        {isCreation ? (
                            <>
                                {activityItem.user.name} created this task -{" "}
                                {formatter.formatRelativeDate(activityItem.createdAt)}
                            </>
                        ) : (
                            <>
                                {activityItem.user.name} edited '{attributeName}' to '
                                {getActivityItemValue(activityItem, formatter)}' -{" "}
                                {formatter.formatRelativeDate(activityItem.createdAt)}
                            </>
                        )}
                    </div>
                }
            ></TooltipTrigger>
            <TooltipContent>{formatter.formatDateTime(activityItem.createdAt)}</TooltipContent>
        </Tooltip>
    );
}
